<template>
  <div class="form-wrapper">
    <SmallLogo />
    <h1 class="onboarding_form_title">
      {{ translate('welcomeToSuperThread') }}
    </h1>
    <div class="flex flex-col gap-4">
      <ProviderLoginButton
        v-for="button in loginButtons"
        :key="button.id"
        :provider-id="button.id"
        :logo-name="button.logo"
        :button-label="button.label"
        @loginClick="handleProviderLoginClick"
      />
    </div>
    <TextSeparator :text="translate('or')" />
    <div class="pb-12">
      <EmailField
        ref="loginFormEmail"
        :foucus-field="true"
        @enter-key="loginProcedure"
      />
      <ButtonCustom
        v-if="!loading"
        class="login-button"
        :type="'important'"
        :text="translate('continueWithEmail')"
        @click="loginProcedure"
      />
      <div
        v-if="loading"
        :class="{ 'login-progress': loading }"
        class="login-button"
      >
        {{ translate('loggingIn') }}
      </div>
    </div>
  </div>
</template>

<script>
import SmallLogo from '@/components/elements/SmallLogo';
import EmailField from '@/components/elements/EmailField';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ProviderLoginButton from '@/components/elements/ProviderLoginButton';
import TextSeparator from '@/components/widgets/TextSeparator';
import EventBus from '@/utilities/eventBus';
import loginMixin from '@/mixins/loginMixin';
import {
  browserStorageSetItem,
  browserStorageGetItem,
  browserStorageRemoveItem,
} from '@/utilities/browser-storage.util';
import {
  loggingInDifferentEmailKey,
  loggedOutGoogleAccountKey,
  loggedOutAppleAccountKey,
} from '@/types/login';
import getRouteAfterLogin from '@/utilities/getRouteAfterLogin';
import useProviderLogin from '@/utilities/composables/useProviderLogin';

export default {
  name: 'LoginForm',
  components: {
    SmallLogo,
    EmailField,
    ButtonCustom,
    ProviderLoginButton,
    TextSeparator,
  },
  mixins: [loginMixin],

  setup() {
    const { clickHandler, logIn: providerLogIn } = useProviderLogin();

    return {
      clickHandler,
      providerLogIn,
    };
  },

  data() {
    return {
      rememberMe: false,
      loading: false,
    };
  },

  computed: {
    appleLogoName() {
      return this.$store.getters.getTheme === 'dark' ? 'apple-logo' : 'apple-logo-dark';
    },
    loginButtons() {
      return [
        {
          id: 'google',
          logo: 'google-logo',
          label: this.getProviderLoginButtonLabel('Google'),
        },
        {
          id: 'apple',
          logo: this.appleLogoName,
          label: this.getProviderLoginButtonLabel('Apple'),
        },
      ];
    },
  },

  beforeCreate() {
    if (this.$route.params.team_subdomain) {
      this.$store.dispatch('setTeamSubDomain', this.$route.params.team_subdomain);
    }
  },
  created() {
    const loggedOutGoogleAccount = browserStorageGetItem(loggedOutGoogleAccountKey);
    const loggedOutAppleAccount = browserStorageGetItem(loggedOutAppleAccountKey);

    if (loggedOutGoogleAccount) {
      this.providerLogIn('google');
      browserStorageRemoveItem(loggedOutGoogleAccountKey);
    } else if (loggedOutAppleAccount) {
      this.providerLogIn('apple');
      browserStorageRemoveItem(loggedOutAppleAccountKey);
    }

    window.addEventListener('resize', this.scrollToBtm);

    const email = browserStorageGetItem(loggingInDifferentEmailKey);
    if (email) this.login(email);

    this.setRedirectRouteAfterLogin();
  },
  unmounted() {
    window.removeEventListener('resize', this.scrollToBtm);
    browserStorageRemoveItem(loggingInDifferentEmailKey);
  },

  methods: {
    async loginProcedure() {
      const email = this.$store.getters.getEmail;
      const user = this.$store.getters.getUser || {};

      if (user.email === email) {
        this.$router.push({ path: this.$store.getters.getTeamSlug });
        return;
      }

      if (this.$refs.loginFormEmail.validate()) return;
      this.loading = true;

      if (user.id) {
        await this.$store.dispatch('logOut');
        browserStorageSetItem(loggingInDifferentEmailKey, email);
        return;
      }

      this.login();
    },

    setRedirectRouteAfterLogin() {
      const route = getRouteAfterLogin(false);
      this.$store.dispatch('setRedirectRouteAfterLogin', route);
    },

    login(email) {
      return this.$store
        .dispatch('sendLoginCodeEmail', { email })
        .then(() => {
          browserStorageRemoveItem('redirectRouteAfterLogin');
          this.$router.push({ name: 'verify-email' });
          this.$store.dispatch('setOnboardingErrorMessage', '');
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          return error;
        });
    },

    toggleRememberMe() {
      this.rememberMe = !this.rememberMe;
    },
    focusEmail() {
      EventBus.$emit('focusEmail');
    },

    getProviderLoginButtonLabel(provider) {
      return this.$route.name === 'create'
        ? this.translate('registerProvider', { provider })
        : this.translate('continueWithProvider', { provider });
    },

    handleProviderLoginClick(providerId) {
      this.clickHandler(providerId);
    },
  },
};
</script>
